import { useState, useMemo } from 'react'
import type { CanonicalComponent, EventSeverity } from '@atmvision/types'
import { useTheme } from '../hooks/useTheme'

const SEV_COLOR: Record<EventSeverity, string> = {
  CRITICAL: '#ef4444', HIGH: '#f97316', WARNING: '#F89421', INFO: '#02AFF0', OK: '#22c55e',
}

interface CatalogEntry {
  code: string
  component: CanonicalComponent
  description: string
  severity: EventSeverity
  action: string
  models: Record<string, string>
}

const CATALOG: CatalogEntry[] = [
  // ── CDM ──────────────────────────────────────────────────────────────────
  // SS-22/22E use NCR APTRA/NDC M-bb status codes (device E = Currency Dispenser)
  // SS-32/38/62/82/84/88 use NCR Easypoint numeric codes (HAWK/MCDM/TCDM/FUJI variants)
  { code: 'ATM-CDM-001', component: 'CDM', severity: 'CRITICAL',
    description: 'Dispensador bloqueado — atasco en camino de transporte',
    action: 'Inspeccionar camino de billetes; limpiar rodillos; resetear CDM',
    models: {
      'NCR Selfserv 22':     'M-12',  'NCR Selfserv 22E': 'M-12',    // NDC: transport jam
      'NCR Selfserv 23':     'CDM_JAM', 'NCR Selfserv 26': 'CDM_JAM', 'NCR Selfserv 27': 'CDM_JAM',
      'NCR Selfserv 32':     '013',   'NCR Selfserv 38': '013',        // Easypoint: cassette transport jam
      'NCR Selfserv 62':     '013',
      'NCR Selfserv 82':     '013',
      'NCR Selfserv 84':     '013',   'NCR Selfserv 88': '013',
      'GRG H22VL':           'CDM_JAM_01',
    },
  },
  { code: 'ATM-CDM-002', component: 'CDM', severity: 'HIGH',
    description: 'Error de sensor de detección de billetes (S1)',
    action: 'Limpiar sensor S1; calibrar; reemplazar si falla — código 1341 para sensor S2',
    models: {
      'NCR Selfserv 22':     'M-32',  'NCR Selfserv 22E': 'M-32',    // NDC: S1 sensor fault
      'NCR Selfserv 23':     'CNT_ERR', 'NCR Selfserv 26': 'CNT_ERR', 'NCR Selfserv 27': 'CNT_ERR',
      'NCR Selfserv 32':     '1340',  'NCR Selfserv 38': '1340',       // Easypoint: S1 sensor (1341 = S2)
      'NCR Selfserv 62':     '1340',
      'NCR Selfserv 82':     '1340',
      'NCR Selfserv 84':     '1340',  'NCR Selfserv 88': '1340',
    },
  },
  { code: 'ATM-CDM-003', component: 'CDM', severity: 'WARNING',
    description: 'Nivel de efectivo bajo en cassette',
    action: 'Programar reposición en próximas 4h; notificar brigada de encashment',
    models: {
      'NCR Selfserv 22':     'M-20',  'NCR Selfserv 22E': 'M-20',    // NDC: cassette 1 low (M-21/22/23 = cass 2/3/4)
      'NCR Selfserv 23':     'CASH_LOW', 'NCR Selfserv 26': 'CASH_LOW', 'NCR Selfserv 27': 'CASH_LOW',
      'NCR Selfserv 32':     '098',   'NCR Selfserv 38': '098',        // Easypoint: cassette low
      'NCR Selfserv 62':     '098',
      'NCR Selfserv 82':     '098',
      'NCR Selfserv 84':     '098',   'NCR Selfserv 88': '098',
      'GRG H22VL':           'CASH_LVL_LOW',
    },
  },
  { code: 'ATM-CDM-004', component: 'CDM', severity: 'HIGH',
    description: 'Cassette de efectivo no presente / removido',
    action: 'Verificar log de acceso al safe; reinstalar cassette; revisar CCTV si no autorizado',
    models: {
      'NCR Selfserv 22':     'M-25',  'NCR Selfserv 22E': 'M-25',    // NDC: cassette 1 missing (M-26/27/28 = 2/3/4)
      'NCR Selfserv 23':     'CSST_RMV', 'NCR Selfserv 26': 'CSST_RMV', 'NCR Selfserv 27': 'CSST_RMV',
      'NCR Selfserv 32':     '099',   'NCR Selfserv 38': '099',        // Easypoint: cassette missing
      'NCR Selfserv 62':     '099',
      'NCR Selfserv 82':     '099',
      'NCR Selfserv 84':     '099',   'NCR Selfserv 88': '099',
    },
  },
  { code: 'ATM-CDM-005', component: 'CDM', severity: 'CRITICAL',
    description: 'Fallo de shutter — camino de transporte bloqueado',
    action: 'Verificar shutter y retract bin; desbloquear manualmente con supervisión',
    models: {
      'NCR Selfserv 22':     'M-10',  'NCR Selfserv 22E': 'M-10',    // NDC: shutter jam
      'NCR Selfserv 23':     'RTR_FAIL', 'NCR Selfserv 26': 'RTR_FAIL', 'NCR Selfserv 27': 'RTR_FAIL',
      'NCR Selfserv 32':     '2301',  'NCR Selfserv 38': '2301',       // Easypoint: transport path blocked
      'NCR Selfserv 62':     '2301',
      'NCR Selfserv 84':     '2301',  'NCR Selfserv 88': '2301',
    },
  },
  { code: 'ATM-CDM-006', component: 'CDM', severity: 'WARNING',
    description: 'Cassette vacío — efectivo agotado',
    action: 'Reposición inmediata requerida; redirigir clientes a ATM más cercano',
    models: {
      'NCR Selfserv 22':     'M-15',  'NCR Selfserv 22E': 'M-15',    // NDC: cassette 1 empty (M-16/17/18 = 2/3/4)
      'NCR Selfserv 23':     'NO_CASH', 'NCR Selfserv 26': 'NO_CASH', 'NCR Selfserv 27': 'NO_CASH',
      'NCR Selfserv 32':     '097',   'NCR Selfserv 38': '097',        // Easypoint: cassette empty
      'NCR Selfserv 62':     '097',
      'NCR Selfserv 82':     '097',
      'NCR Selfserv 84':     '097',   'NCR Selfserv 88': '097',
      'GRG H22VL':           'CASH_LVL_EMPTY',
    },
  },
  { code: 'ATM-CDM-007', component: 'CDM', severity: 'CRITICAL',
    description: 'Fallo de cinta transportadora / motor de dispenser',
    action: 'Requiere técnico presencial; no forzar mecanismo; poner fuera de servicio',
    models: {
      'NCR Selfserv 22':     'M-71',  'NCR Selfserv 22E': 'M-71',    // NDC: belt failure (M-70 = motor stall)
      'NCR Selfserv 23':     'BELT_FAIL', 'NCR Selfserv 26': 'BELT_FAIL', 'NCR Selfserv 27': 'BELT_FAIL',
      'NCR Selfserv 32':     '998',   'NCR Selfserv 38': '998',        // Easypoint: dispenser device failure
      'NCR Selfserv 82':     '998',
      'NCR Selfserv 84':     '998',   'NCR Selfserv 88': '998',
    },
  },
  { code: 'ATM-CDM-008', component: 'CDM', severity: 'HIGH',
    description: 'Error de calidad de billete — doble detección o sesgo',
    action: 'Verificar condición de billetes en cassette; limpiar detector UV/IR; rechazar lote afectado',
    models: {
      'NCR Selfserv 22':     'M-05',  'NCR Selfserv 22E': 'M-05',    // NDC: double detect (M-08 = skew)
      'NCR Selfserv 23':     'CDM_NOTE_ERR', 'NCR Selfserv 26': 'CDM_NOTE_ERR', 'NCR Selfserv 27': 'CDM_NOTE_ERR',
      'NCR Selfserv 32':     '068',   'NCR Selfserv 38': '068',        // Easypoint: general dispenser failure
      'NCR Selfserv 62':     '068',
      'NCR Selfserv 84':     '068',   'NCR Selfserv 88': '068',
    },
  },
  { code: 'ATM-CDM-009', component: 'CDM', severity: 'HIGH',
    description: 'Papelera de rechazo llena',
    action: 'Vaciar reject bin; contabilizar billetes rechazados; reiniciar CDM tras vaciado',
    models: {
      'NCR Selfserv 22':     'M-13',  'NCR Selfserv 22E': 'M-13',    // NDC: reject bin full (M-14 = bin missing)
      'NCR Selfserv 23':     'CDM_REJECT_FULL', 'NCR Selfserv 26': 'CDM_REJECT_FULL', 'NCR Selfserv 27': 'CDM_REJECT_FULL',
      'NCR Selfserv 32':     'CDM_REJECT_FULL', 'NCR Selfserv 38': 'CDM_REJECT_FULL',
      'NCR Selfserv 84':     'CDM_REJECT_FULL', 'NCR Selfserv 88': 'CDM_REJECT_FULL',
    },
  },

  // ── IDC ──────────────────────────────────────────────────────────────────
  // IDC codes for SS-22/22E use APTRA device-C fault format (not M-bb which is CDM-only)
  { code: 'ATM-IDC-001', component: 'IDC', severity: 'HIGH',
    description: 'Tarjeta atascada en lector',
    action: 'Verificar lector; cliente debe contactar banco para recuperar tarjeta',
    models: {
      'NCR Selfserv 22':     '0xC101', 'NCR Selfserv 22E': '0xC101',
      'NCR Selfserv 23':     'IDC_JAM', 'NCR Selfserv 26': 'IDC_JAM', 'NCR Selfserv 27': 'IDC_JAM',
      'NCR Selfserv 32':     'IDC_JAM', 'NCR Selfserv 38': 'IDC_JAM',
      'NCR Selfserv 82':     'CARD_JAM',
      'NCR Selfserv 84':     'IDC_JAM', 'NCR Selfserv 88': 'IDC_JAM',
      'GRG H22VL':           'IDC_01_JAM',
    },
  },
  { code: 'ATM-IDC-002', component: 'IDC', severity: 'HIGH',
    description: 'Fallo de lector de banda magnética',
    action: 'Limpiar cabezal; verificar alineación; reemplazar si persiste',
    models: {
      'NCR Selfserv 22':     '0xC202', 'NCR Selfserv 22E': '0xC202',
      'NCR Selfserv 23':     'MAG_FAIL', 'NCR Selfserv 26': 'MAG_FAIL', 'NCR Selfserv 27': 'MAG_FAIL',
      'NCR Selfserv 32':     'MAG_FAIL', 'NCR Selfserv 38': 'MAG_FAIL',
      'NCR Selfserv 82':     'MAGSTRIPE_ERR',
      'NCR Selfserv 84':     'MAG_FAIL',
      'GRG H22VL':           'IDC_MAG_ERR',
    },
  },
  { code: 'ATM-IDC-003', component: 'IDC', severity: 'WARNING',
    description: 'Fallo de lector EMV/chip',
    action: 'Limpiar contactos; verificar módulo EMV; actualizar firmware si disponible',
    models: {
      'NCR Selfserv 23':     'EMV_FAIL', 'NCR Selfserv 26': 'EMV_FAIL', 'NCR Selfserv 27': 'EMV_FAIL',
      'NCR Selfserv 32':     'EMV_FAIL', 'NCR Selfserv 38': 'EMV_FAIL',
      'NCR Selfserv 62':     'CHIP_ERR',
      'NCR Selfserv 84':     'EMV_FAIL', 'NCR Selfserv 88': 'EMV_FAIL',
    },
  },
  { code: 'ATM-IDC-004', component: 'IDC', severity: 'WARNING',
    description: 'Lector de tarjetas en modo supervisor',
    action: 'Salir de modo supervisor; verificar que no haya sesión activa',
    models: {
      'NCR Selfserv 23':     'SUPERVISOR_MODE', 'NCR Selfserv 26': 'SUPERVISOR_MODE', 'NCR Selfserv 27': 'SUPERVISOR_MODE',
      'NCR Selfserv 32':     'SUPERVISOR_MODE', 'NCR Selfserv 38': 'SUPERVISOR_MODE',
      'GRG H22VL':           'IDC_S04',
    },
  },

  // ── NET ──────────────────────────────────────────────────────────────────
  // Easypoint comms codes apply to models with Easypoint platform (SS-32/38/62/82/84/88)
  { code: 'ATM-NET-001', component: 'NET', severity: 'HIGH',
    description: 'Sin conectividad IP — enlace caído',
    action: 'Verificar cable LAN/3G; reiniciar router; contactar proveedor si persiste',
    models: {
      'NCR Selfserv 22':     '0xN001', 'NCR Selfserv 22E': '0xN001',
      'NCR Selfserv 23':     'NET_DOWN', 'NCR Selfserv 26': 'NET_DOWN', 'NCR Selfserv 27': 'NET_DOWN',
      'NCR Selfserv 32':     '002',   'NCR Selfserv 38': '002',          // Easypoint: comms link down
      'NCR Selfserv 62':     '002',
      'NCR Selfserv 82':     '002',
      'NCR Selfserv 84':     '002',   'NCR Selfserv 88': '002',
      'GRG H22VL':           'NET_OFFLINE',
    },
  },
  { code: 'ATM-NET-002', component: 'NET', severity: 'HIGH',
    description: 'Timeout / fallo de enlace PPP con host',
    action: 'Verificar ruta a host; revisar firewall; reiniciar stack TCP/PPP',
    models: {
      'NCR Selfserv 22':     '0xN002', 'NCR Selfserv 22E': '0xN002',
      'NCR Selfserv 23':     'HOST_TIMEOUT', 'NCR Selfserv 26': 'HOST_TIMEOUT', 'NCR Selfserv 27': 'HOST_TIMEOUT',
      'NCR Selfserv 32':     '970',   'NCR Selfserv 38': '970',          // Easypoint: PPP link failure
      'NCR Selfserv 62':     '970',
      'NCR Selfserv 84':     '970',   'NCR Selfserv 88': '970',
    },
  },
  { code: 'ATM-NET-003', component: 'NET', severity: 'WARNING',
    description: 'Latencia elevada con host central',
    action: 'Monitorear; investigar congestión de red; revisar QoS',
    models: {
      'NCR Selfserv 23':     'HIGH_LATENCY', 'NCR Selfserv 26': 'HIGH_LATENCY', 'NCR Selfserv 27': 'HIGH_LATENCY',
      'NCR Selfserv 32':     'HIGH_LATENCY', 'NCR Selfserv 38': 'HIGH_LATENCY',
      'NCR Selfserv 84':     'HIGH_LATENCY', 'NCR Selfserv 88': 'HIGH_LATENCY',
    },
  },

  // ── PTR ──────────────────────────────────────────────────────────────────
  { code: 'ATM-PTR-001', component: 'PTR', severity: 'HIGH',
    description: 'Sin papel en impresora de recibos',
    action: 'Reposición de papel; reiniciar impresora tras carga',
    models: {
      'NCR Selfserv 22':     '0xP001', 'NCR Selfserv 22E': '0xP001',
      'NCR Selfserv 23':     'PTR_NO_PAPER', 'NCR Selfserv 26': 'PTR_NO_PAPER', 'NCR Selfserv 27': 'PTR_NO_PAPER',
      'NCR Selfserv 32':     'NO_PAPER', 'NCR Selfserv 38': 'NO_PAPER',
      'NCR Selfserv 62':     'NO_PAPER',
      'NCR Selfserv 82':     'PAPER_OUT',
      'NCR Selfserv 84':     'PTR_NO_PAPER', 'NCR Selfserv 88': 'PTR_NO_PAPER',
      'GRG H22VL':           'PTR_EMPTY',
    },
  },
  { code: 'ATM-PTR-002', component: 'PTR', severity: 'HIGH',
    description: 'Papel atascado en impresora',
    action: 'Abrir tapa; remover papel atascado; evitar forzar mecanismo',
    models: {
      'NCR Selfserv 22':     '0xP002', 'NCR Selfserv 22E': '0xP002',
      'NCR Selfserv 23':     'PTR_JAM', 'NCR Selfserv 26': 'PTR_JAM', 'NCR Selfserv 27': 'PTR_JAM',
      'NCR Selfserv 32':     'PTR_JAM', 'NCR Selfserv 38': 'PTR_JAM',
      'NCR Selfserv 82':     'PAPER_JAM',
      'NCR Selfserv 84':     'PTR_JAM', 'NCR Selfserv 88': 'PTR_JAM',
    },
  },

  // ── SEC ──────────────────────────────────────────────────────────────────
  { code: 'ATM-SEC-001', component: 'SEC', severity: 'CRITICAL',
    description: 'Puerta del safe abierta',
    action: 'Alerta de seguridad inmediata; verificar personal autorizado; revisar CCTV',
    models: {
      'NCR Selfserv 22':     '0xS001', 'NCR Selfserv 22E': '0xS001',
      'NCR Selfserv 23':     'SAFE_OPEN', 'NCR Selfserv 26': 'SAFE_OPEN', 'NCR Selfserv 27': 'SAFE_OPEN',
      'NCR Selfserv 32':     'SAFE_OPEN', 'NCR Selfserv 38': 'SAFE_OPEN',
      'NCR Selfserv 82':     'DOOR_OPEN',
      'NCR Selfserv 84':     'SAFE_OPEN', 'NCR Selfserv 88': 'SAFE_OPEN',
    },
  },
  { code: 'ATM-SEC-002', component: 'SEC', severity: 'CRITICAL',
    description: 'Intento de skimming detectado',
    action: 'Poner fuera de servicio inmediatamente; notificar seguridad; preservar evidencia',
    models: {
      'NCR Selfserv 84':     'SKIM_DETECT', 'NCR Selfserv 88': 'SKIM_DETECT',
      'NCR Selfserv 6682 FA':'SKIM_DETECT', 'NCR Selfserv 6682 RA': 'SKIM_DETECT',
    },
  },

  // ── BNA ──────────────────────────────────────────────────────────────────
  { code: 'ATM-BNA-001', component: 'BNA', severity: 'HIGH',
    description: 'Módulo BNA — error de validación de billetes',
    action: 'Calibrar BNA; verificar suciedad; limpiar sensores UV/IR',
    models: {
      'NCR Selfserv 6682 FA': 'BNA_VAL_ERR', 'NCR Selfserv 6682 RA': 'BNA_VAL_ERR',
    },
  },

  // ── SYS ──────────────────────────────────────────────────────────────────
  { code: 'ATM-SYS-001', component: 'SYS', severity: 'HIGH',
    description: 'Reinicio inesperado del sistema operativo',
    action: 'Revisar logs de Windows/Linux; verificar fuente de poder; analizar BSOD',
    models: {
      'NCR Selfserv 22':     '0xX001', 'NCR Selfserv 22E': '0xX001',
      'NCR Selfserv 23':     'SYS_REBOOT', 'NCR Selfserv 26': 'SYS_REBOOT', 'NCR Selfserv 27': 'SYS_REBOOT',
      'NCR Selfserv 32':     'SYS_REBOOT', 'NCR Selfserv 38': 'SYS_REBOOT',
      'NCR Selfserv 62':     'SYS_REBOOT',
      'NCR Selfserv 82':     'UNEXPECTED_REBOOT',
      'NCR Selfserv 84':     'SYS_REBOOT', 'NCR Selfserv 88': 'SYS_REBOOT',
    },
  },
  { code: 'ATM-SYS-002', component: 'SYS', severity: 'WARNING',
    description: 'Espacio en disco bajo',
    action: 'Limpiar logs antiguos; verificar journaling; añadir almacenamiento si persistente',
    models: {
      'NCR Selfserv 23':     'DISK_LOW', 'NCR Selfserv 26': 'DISK_LOW', 'NCR Selfserv 27': 'DISK_LOW',
      'NCR Selfserv 32':     '960',   'NCR Selfserv 38': '960',          // Easypoint: disk space critical
      'NCR Selfserv 62':     '960',
      'NCR Selfserv 82':     '960',
      'NCR Selfserv 84':     '960',   'NCR Selfserv 88': '960',
    },
  },
  { code: 'ATM-SYS-003', component: 'SYS', severity: 'INFO',
    description: 'Reinicio programado de aplicación',
    action: 'Normal operation; verificar que reinicio complete en tiempo esperado',
    models: {
      'NCR Selfserv 23':     'APP_RESTART', 'NCR Selfserv 26': 'APP_RESTART', 'NCR Selfserv 27': 'APP_RESTART',
      'NCR Selfserv 32':     'APP_RESTART', 'NCR Selfserv 38': 'APP_RESTART',
      'NCR Selfserv 82':     'APP_RESTART',
      'NCR Selfserv 84':     'APP_RESTART', 'NCR Selfserv 88': 'APP_RESTART',
    },
  },
  { code: 'ATM-SYS-004', component: 'SYS', severity: 'WARNING',
    description: 'Versión de firmware de dispenser no coincide',
    action: 'Actualizar firmware del dispenser; verificar compatibilidad con versión APTRA instalada',
    models: {
      'NCR Selfserv 32':     '2290',  'NCR Selfserv 38': '2290',         // Easypoint: firmware version mismatch
      'NCR Selfserv 62':     '2290',
      'NCR Selfserv 82':     '2290',
      'NCR Selfserv 84':     '2290',  'NCR Selfserv 88': '2290',
    },
  },

  // ── UPS ──────────────────────────────────────────────────────────────────
  { code: 'ATM-UPS-001', component: 'UPS', severity: 'WARNING',
    description: 'Batería UPS con carga baja',
    action: 'Programar reemplazo de batería; monitorear cortes de energía',
    models: {
      'NCR Selfserv 22':     'UPS_BATT_LOW', 'NCR Selfserv 22E': 'UPS_BATT_LOW',
      'NCR Selfserv 23':     'UPS_BATT_LOW', 'NCR Selfserv 26': 'UPS_BATT_LOW', 'NCR Selfserv 27': 'UPS_BATT_LOW',
      'NCR Selfserv 32':     'UPS_BATT_LOW', 'NCR Selfserv 38': 'UPS_BATT_LOW',
      'NCR Selfserv 82':     'BATTERY_LOW',
      'NCR Selfserv 84':     'UPS_BATT_LOW', 'NCR Selfserv 88': 'UPS_BATT_LOW',
    },
  },
  { code: 'ATM-UPS-002', component: 'UPS', severity: 'CRITICAL',
    description: 'UPS en modo batería — falla energía comercial',
    action: 'Verificar suministro eléctrico; monitorear tiempo en batería; preparar apagado seguro',
    models: {
      'NCR Selfserv 22':     '0xU002',  'NCR Selfserv 22E': '0xU002',
      'NCR Selfserv 23':     'ON_BATTERY', 'NCR Selfserv 26': 'ON_BATTERY', 'NCR Selfserv 27': 'ON_BATTERY',
      'NCR Selfserv 32':     'ON_BATTERY', 'NCR Selfserv 38': 'ON_BATTERY',
      'NCR Selfserv 82':     'AC_FAIL',
      'NCR Selfserv 84':     'ON_BATTERY', 'NCR Selfserv 88': 'ON_BATTERY',
    },
  },
]

const COMPONENTS: CanonicalComponent[] = ['CDM', 'IDC', 'NET', 'PTR', 'SEC', 'BNA', 'SYS', 'UPS']
const ALL_MODELS = [
  'NCR Selfserv 22', 'NCR Selfserv 22E',
  'NCR Selfserv 23', 'NCR Selfserv 26', 'NCR Selfserv 27',
  'NCR Selfserv 32', 'NCR Selfserv 38',
  'NCR Selfserv 62',
  'NCR Selfserv 82', 'NCR Selfserv 84', 'NCR Selfserv 88',
  'NCR Selfserv 6682 FA', 'NCR Selfserv 6682 RA',
  'GRG H22VL',
]

export default function ErrorCatalog() {
  const C = useTheme()
  const [selectedCode, setSelectedCode] = useState<string>(CATALOG[0].code)
  const [search, setSearch] = useState('')
  const [sevFilter, setSevFilter] = useState<EventSeverity | 'ALL'>('ALL')

  const filtered = useMemo(() => {
    const q = search.toLowerCase()
    return CATALOG.filter((e) => {
      if (q && !e.code.toLowerCase().includes(q) && !e.description.toLowerCase().includes(q)) return false
      if (sevFilter !== 'ALL' && e.severity !== sevFilter) return false
      return true
    })
  }, [search, sevFilter])

  const byComponent = useMemo(() => {
    const acc: Record<CanonicalComponent, CatalogEntry[]> = {} as Record<CanonicalComponent, CatalogEntry[]>
    COMPONENTS.forEach((c) => { acc[c] = [] })
    filtered.forEach((e) => { acc[e.component]?.push(e) })
    return acc
  }, [filtered])

  const selected = CATALOG.find((e) => e.code === selectedCode)

  // Coverage table
  const coverage = useMemo(() => {
    return ALL_MODELS.map((model) => {
      const total = CATALOG.length
      const mapped = CATALOG.filter((e) => model in e.models).length
      return { model, mapped, total, pct: Math.round((mapped / total) * 100) }
    })
  }, [])

  return (
    <div style={{ flex: 1, display: 'flex', overflow: 'hidden', background: C.dark }}>
      {/* Left Sidebar */}
      <div style={{ width: 260, background: C.card, borderRight: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0 }}>
        <div style={{ padding: '10px 12px', borderBottom: `1px solid ${C.border}` }}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar código o descripción..."
            style={{ width: '100%', background: C.card2, border: `1px solid ${C.border}`, color: C.text, borderRadius: 6, padding: '6px 10px', fontSize: 11, fontFamily: 'inherit', marginBottom: 8 }}
          />
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {(['ALL', 'CRITICAL', 'HIGH', 'WARNING', 'INFO'] as Array<EventSeverity | 'ALL'>).map((s) => (
              <button key={s} onClick={() => setSevFilter(s)} style={{
                background: sevFilter === s ? (s === 'ALL' ? C.primary : `${SEV_COLOR[s as EventSeverity]}22`) : 'transparent',
                border: `1px solid ${s === 'ALL' ? C.border : SEV_COLOR[s as EventSeverity] ?? C.border}`,
                color: s === 'ALL' ? C.muted : SEV_COLOR[s as EventSeverity], padding: '2px 7px',
                borderRadius: 4, cursor: 'pointer', fontSize: 10, fontFamily: 'inherit', fontWeight: 600,
              }}>
                {s === 'ALL' ? 'Todos' : s}
              </button>
            ))}
          </div>
        </div>

        <div style={{ flex: 1, overflow: 'auto' }}>
          {COMPONENTS.map((comp) => {
            const entries = byComponent[comp]
            if (entries.length === 0) return null
            return (
              <div key={comp}>
                <div style={{ padding: '8px 12px 4px', fontSize: 10, color: C.blue, textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600, borderBottom: `1px solid ${C.border}` }}>
                  {comp} — {entries.length} código{entries.length !== 1 ? 's' : ''}
                </div>
                {entries.map((e) => (
                  <div
                    key={e.code}
                    onClick={() => setSelectedCode(e.code)}
                    style={{
                      padding: '8px 12px', cursor: 'pointer',
                      background: selectedCode === e.code ? 'rgba(38,78,114,0.4)' : 'transparent',
                      borderLeft: selectedCode === e.code ? `3px solid ${C.blue}` : '3px solid transparent',
                      borderBottom: `1px solid ${C.border}`,
                      transition: 'background 0.1s',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ width: 7, height: 7, borderRadius: '50%', background: SEV_COLOR[e.severity], flexShrink: 0 }} />
                      <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: C.text, fontWeight: 600 }}>{e.code}</span>
                    </div>
                    <div style={{ fontSize: 10, color: C.muted, marginTop: 2, paddingLeft: 13 }}>{e.description}</div>
                  </div>
                ))}
              </div>
            )
          })}
        </div>
      </div>

      {/* Right Panel */}
      <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
        {selected ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 900 }}>
            {/* Header */}
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '16px 20px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
                <div>
                  <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 22, fontWeight: 700, color: C.text, marginBottom: 4 }}>
                    {selected.code}
                  </div>
                  <div style={{ fontSize: 15, color: C.text, marginBottom: 8 }}>{selected.description}</div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <span style={{ padding: '4px 10px', borderRadius: 6, background: `${SEV_COLOR[selected.severity]}20`, border: `1px solid ${SEV_COLOR[selected.severity]}60`, color: SEV_COLOR[selected.severity], fontSize: 12, fontWeight: 700 }}>
                    {selected.severity}
                  </span>
                  <span style={{ padding: '4px 10px', borderRadius: 6, background: 'rgba(2,175,240,0.1)', border: '1px solid rgba(2,175,240,0.3)', color: C.blue, fontSize: 12 }}>
                    {selected.component}
                  </span>
                </div>
              </div>
              <div style={{ background: C.card2, borderRadius: 8, padding: '10px 14px', borderLeft: `3px solid ${C.orange}` }}>
                <div style={{ fontSize: 10, color: C.orange, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, marginBottom: 4 }}>
                  Acción Recomendada
                </div>
                <div style={{ fontSize: 13, color: C.text }}>{selected.action}</div>
              </div>
            </div>

            {/* Model Mappings */}
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '16px 20px' }}>
              <div style={{ fontSize: 11, color: C.blue, textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600, marginBottom: 14 }}>
                Mapeo de Códigos por Modelo ({Object.keys(selected.models).length} modelos)
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
                {ALL_MODELS.map((model) => {
                  const raw = selected.models[model]
                  return (
                    <div key={model} style={{
                      background: raw ? 'rgba(2,175,240,0.06)' : 'rgba(255,255,255,0.02)',
                      border: `1px solid ${raw ? 'rgba(2,175,240,0.25)' : C.border}`,
                      borderRadius: 7, padding: '8px 12px',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    }}>
                      <span style={{ fontSize: 11, color: raw ? C.text : C.muted }}>{model}</span>
                      {raw ? (
                        <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: C.blue, fontWeight: 600 }}>{raw}</span>
                      ) : (
                        <span style={{ fontSize: 10, color: '#4a5568', fontStyle: 'italic' }}>Sin mapear</span>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Coverage Table */}
            <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '16px 20px' }}>
              <div style={{ fontSize: 11, color: C.blue, textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600, marginBottom: 14 }}>
                Cobertura del Catálogo por Modelo
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                    <th style={{ padding: '8px 10px', textAlign: 'left', color: C.muted, fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Modelo</th>
                    <th style={{ padding: '8px 10px', textAlign: 'right', color: C.muted, fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Mapeados</th>
                    <th style={{ padding: '8px 10px', textAlign: 'right', color: C.muted, fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Total</th>
                    <th style={{ padding: '8px 10px', color: C.muted, fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Cobertura</th>
                  </tr>
                </thead>
                <tbody>
                  {coverage.map(({ model, mapped, total, pct }) => (
                    <tr key={model} style={{ borderBottom: `1px solid ${C.border}` }}>
                      <td style={{ padding: '8px 10px', color: C.text }}>{model}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', color: C.muted }}>{mapped}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', color: C.muted }}>{total}</td>
                      <td style={{ padding: '8px 10px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ flex: 1, height: 6, background: 'rgba(255,255,255,0.08)', borderRadius: 3 }}>
                            <div style={{ height: '100%', width: `${pct}%`, background: pct >= 70 ? C.green : pct >= 40 ? C.orange : C.red, borderRadius: 3 }} />
                          </div>
                          <span style={{ fontSize: 11, color: pct >= 70 ? C.green : pct >= 40 ? C.orange : C.red, fontWeight: 600, minWidth: 32, textAlign: 'right' }}>
                            {pct}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: C.muted }}>
            Selecciona un código del catálogo
          </div>
        )}
      </div>
    </div>
  )
}
